/**
 * Einstellungen der automatischen Sicherung (F-46) - die einzige Stelle, die
 * diese Schluessel anfasst.
 *
 * Sie liegen in der lokalen Tabelle `meta`. Die steht NICHT in
 * `DATA_TABLE_NAMES`: Sie wird nicht synchronisiert, nicht gesichert und beim
 * Einspielen nicht geleert. Die Einstellung gilt also je Geraet - nach einem
 * Umzug wird der Ordner neu gewaehlt. Den Ordner-Handle speichert IndexedDB
 * per Structured Clone, so wie schon den Sync-Schluessel.
 */
import { db } from '@/db/schema';
import { behaltenAus, zuletztAus, type AutoZuletzt } from '@/domain/sicherung/autoSicherung';
import type { OrdnerHandle } from '@/lib/backup/ordner';

export const META_AUTO_ORDNER = 'autoSicherung.ordner';
export const META_AUTO_BEHALTEN = 'autoSicherung.behalten';
export const META_AUTO_ZULETZT = 'autoSicherung.zuletzt';

function istOrdner(v: unknown): v is OrdnerHandle {
  if (typeof v !== 'object' || v === null) return false;
  const h = v as { kind?: unknown; name?: unknown; getFileHandle?: unknown };
  return h.kind === 'directory' && typeof h.name === 'string' && typeof h.getFileHandle === 'function';
}

/** "An" heisst: ein Ordner ist gespeichert. Einen eigenen Schalter gibt es nicht. */
export async function ordnerLaden(): Promise<OrdnerHandle | null> {
  const v = (await db.meta.get(META_AUTO_ORDNER))?.value;
  return istOrdner(v) ? v : null;
}

export async function ordnerSpeichern(h: OrdnerHandle): Promise<void> {
  await db.meta.put({ key: META_AUTO_ORDNER, value: h });
}

export async function behaltenLaden(): Promise<number> {
  return behaltenAus((await db.meta.get(META_AUTO_BEHALTEN))?.value);
}

export async function behaltenSpeichern(n: number): Promise<void> {
  await db.meta.put({ key: META_AUTO_BEHALTEN, value: behaltenAus(n) });
}

export async function zuletztLaden(): Promise<AutoZuletzt | null> {
  return zuletztAus((await db.meta.get(META_AUTO_ZULETZT))?.value);
}

export async function zuletztSpeichern(z: AutoZuletzt): Promise<void> {
  await db.meta.put({ key: META_AUTO_ZULETZT, value: z });
}

/**
 * Ausschalten: nur der Ordner wird vergessen. Der letzte Stand bleibt - er
 * ist der Vergleichswert, der beim Wiedereinschalten gute Kopien vor dem
 * Verdraengen schuetzt. Die Anzahl bleibt eingestellt.
 */
export async function autoSicherungEntfernen(): Promise<void> {
  await db.meta.delete(META_AUTO_ORDNER);
}
